import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) this.#showResetButton();

    if (this.dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      this.dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }
  }

  #handleModalClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    const isInteractive =
      t instanceof HTMLButtonElement ||
      t instanceof HTMLAnchorElement ||
      t instanceof HTMLInputElement ||
      t.closest('button') ||
      t.closest('a') ||
      t.closest('input');
    if (!isInteractive && this.refs.searchInput) this.refs.searchInput.focus();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') this.dialog?.toggleDialog();
  };

  #handleDialogClose = () => this.#resetSearch();

  expandSearch = () => {
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  #isKeyboardNavigation = false;

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll('.predictive-search-results__wrapper-queries, .predictive-search-results__list')
    );
    return /** @type {HTMLElement[]} */ (
      containers.flatMap((c) => Array.from(c.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')))
        .filter((el) => el instanceof HTMLElement)
    );
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((i) => i.getAttribute('aria-selected') === 'true') ?? -1;
  }
  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => item.classList.remove('keyboard-focus'));
    for (const [i, item] of this.#allResultsItems.entries()) {
      if (i === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    const current = this.#currentIndex;
    const total = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = current < total - 1 ? current + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey ? (current > 0 ? current - 1 : total - 1) : (current < total - 1 ? current + 1 : 0);
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = current > 0 ? current - 1 : total - 1;
        break;

      case 'Enter': {
        const single = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (single instanceof HTMLElement && single.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = single.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          searchUrl.searchParams.set('type', 'article');           // ← only articles
          searchUrl.searchParams.set('options[prefix]', 'last');   // keep your current option
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;
    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(term);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (inner instanceof HTMLElement) inner.scrollTop = 0;

      const content = this.querySelector('.predictive-search-form__content');
      if (content instanceof HTMLElement) content.scrollTop = 0;
    });
  }

  async #getSearchResults(term) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);

    // Only articles
    url.searchParams.set('resources[type]', 'article');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[articles][limit]', '8');
    url.searchParams.set('resources[queries][limit]', '6');

    // Make sure products/pages/collections are zeroed out
    url.searchParams.set('resources[products][limit]', '0');
    url.searchParams.set('resources[pages][limit]', '0');
    url.searchParams.set('resources[collections][limit]', '0');

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((markup) => {
        if (!markup || abortController.signal.aborted) return;
        morph(this.refs.predictiveSearchResults, markup);
        this.#resetScrollPositions();
      })
      .catch((err) => {
        if (!abortController.signal.aborted) throw err;
      });
  }

  #hideResetButton() { this.refs.resetButton.hidden = true; }
  #showResetButton() { this.refs.resetButton.hidden = false; }

  #createAbortController() {
    const ac = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = ac;
    return ac;
  }

  #resetSearch = async () => {
    const emptySectionId = 'predictive-search-empty';
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();

    const ac = this.#createAbortController();
    const emptyMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsed = new DOMParser().parseFromString(emptyMarkup, 'text/html').querySelector('.predictive-search-empty-section');
    if (!parsed || ac.signal.aborted) return;

    morph(this.refs.predictiveSearchResults, parsed);
    this.#resetScrollPositions();
  };

  #blurSearch = () => this.refs.searchInput.blur();
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
